import {
  createEditor,
  Editor,
  Transforms,
  type Descendant,
  type Operation,
} from 'slate';
import { suppressNormalizations } from './MarkUtils';
import EditorUtils from './EditorUtils';
import Annotations from '../plugins/AnnotationsPlugin/Annotations';
import type { AnnotationKey } from '../types/aha-editor';

class OperationsUtils {
  // Generate the operations necessary to mutate `editor` to be the same as
  // `newEditor`.
  static operationsForReplace(editor: Editor, newEditor: Editor): Operation[] {
    const opEditor = createEditor(); // Editor we will use to generate the operations.
    opEditor.children = structuredClone(editor.children);
    opEditor.metadata = structuredClone(
      editor.metadata || EditorUtils.defaultMetadata()
    );

    // We completely suppress normalization here since opEditor doesn't know
    // what plugins are available and so the schema is unknown and normalizations
    // might mess up the content. We need to assume that newEditor is already
    // normalized.
    suppressNormalizations(opEditor, () => {
      Transforms.select(opEditor, {
        anchor: Editor.start(opEditor, []),
        focus: Editor.end(opEditor, []),
      });
      // Delete all. The 'highest' here was critical to get it to actually
      // remove everything.
      Transforms.removeNodes(opEditor, {
        hanging: true,
        voids: true,
        mode: 'highest',
      });
      // Insert new content.
      Transforms.insertNodes(opEditor, newEditor.children);
      // Transform the annotations.
      const newAnnotations = newEditor.metadata?.annotations || {};
      const annotations = opEditor.metadata?.annotations || {};

      // Update common annotations.
      Object.keys(annotations)
        .filter((oldKey: AnnotationKey) => newAnnotations[oldKey])
        .forEach((key: AnnotationKey) => {
          const newAnn = newAnnotations[key];
          if (!newAnn) return;

          Annotations.updateAnnotation(opEditor, {
            key,
            newProperties: newAnn,
          });
        });

      // Remove removed annotations
      Object.keys(annotations)
        .filter((oldKey: AnnotationKey) => !newAnnotations[oldKey])
        .forEach((key: AnnotationKey) => {
          Annotations.removeAnnotation(opEditor, { key });
        });

      // Add added annotations
      Object.keys(newAnnotations)
        .filter((newKey: AnnotationKey) => !annotations[newKey])
        .forEach((key: AnnotationKey) => {
          const newAnnotation = newAnnotations[key];
          if (!newAnnotation) return;

          Annotations.addAnnotation(opEditor, {
            key,
            range: newAnnotation.range.current,
            kind: newAnnotation.kind,
            annotationProperties: {},
          });
        });
    });

    return opEditor.operations;
  }

  // Generate the operations necessary append the nodes to the editor.
  static operationsForAppend(editor: Editor, nodes: Descendant[]): Operation[] {
    const opEditor = createEditor(); // Editor we will use to generate the operations.
    opEditor.children = structuredClone(editor.children);
    opEditor.metadata = structuredClone(
      editor.metadata || { annotations: {}, tableOfContentsEnabled: false }
    );

    // We completely suppress normalization here since opEditor doesn't know
    // what plugins are available and so the schema is unknown and normalizations
    // might mess up the content. We need to assume that newEditor is already
    // normalized.
    suppressNormalizations(opEditor, () => {
      // Move cursor to end.
      Transforms.select(opEditor, {
        anchor: Editor.end(opEditor, []),
        focus: Editor.end(opEditor, []),
      });

      // Insert new content.
      Transforms.insertNodes(opEditor, nodes);
    });

    return opEditor.operations;
  }

  static isModifyingOperation(operation: Operation): boolean {
    return ![
      'add_annotation',
      'set_annotation',
      'remove_annotation',
      'set_selection',
    ].includes(operation.type);
  }
}

export default OperationsUtils;
